import { type FormEvent, useState } from "react";
import { toast } from "sonner";
import { MakyMascot } from "@/components/brand/MakyMark";
import { AppShell } from "@/components/layout/AppShell";
import { Button, ListCell, Skeleton, Switch, TextField } from "@/components/ui";
import { useAddPeople, usePeople, useSetPersonActive } from "@/lib/api/queries";
import { useCurrentWorkspace } from "@/stores/workspace";

export default function PeoplePage() {
  const workspaceId = useCurrentWorkspace().workspace?.id ?? "";
  const [name, setName] = useState("");
  const { data: people, isLoading } = usePeople(workspaceId);
  const addPeople = useAddPeople(workspaceId);
  const setActive = useSetPersonActive(workspaceId);

  function handleAdd(event: FormEvent) {
    event.preventDefault();
    if (!name.trim() || addPeople.isPending) return;
    // 여러 줄을 붙여넣으면 그대로 넘긴다 — 서버가 줄 단위로 쪼개고 중복을 걸러낸다.
    addPeople.mutate(name, {
      onSuccess: (created) => {
        setName("");
        toast.success(created.length > 0 ? `${created.length}명 추가했어요` : "이미 명단에 있어요");
      },
      onError: () => toast.error("추가하지 못했어요. 다시 시도해 주세요."),
    });
  }

  return (
    <AppShell title="명단">
      <form className="flex items-end gap-2" onSubmit={handleAdd}>
        <TextField
          label="이름"
          placeholder="예: 임거묵"
          style={{ flex: 1, minWidth: 0 }}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="off"
          enterKeyHint="done"
        />
        <Button
          type="submit"
          style={{ flex: "none" }}
          disabled={addPeople.isPending || !name.trim()}
        >
          추가
        </Button>
      </form>

      {isLoading ? (
        <Skeleton rows={4} className="mt-6" />
      ) : people && people.length > 0 ? (
        <ul className="mt-6">
          {people.map((person) => (
            <li key={person.id}>
              <ListCell
                title={person.name}
                trailing={
                  <Switch
                    checked={person.active}
                    onChange={(e) =>
                      setActive.mutate({ personId: person.id, active: e.target.checked })
                    }
                  />
                }
              />
            </li>
          ))}
        </ul>
      ) : (
        <div className="mt-8 flex flex-col items-center gap-3 text-center">
          <MakyMascot size={96} head />
          <p className="text-body-2 text-label-alternative">
            아직 등록된 사람이 없어요. 위에 이름을 적고 추가해 보세요.
          </p>
        </div>
      )}
    </AppShell>
  );
}
